import crypto from "../include/crypto";
import { HandshakeInfo, HttpRequestInit, SecurequClientConfig, SecurequClientResponse } from "./types";
import xanFetch from 'xanfetch'


const Clients = new Map<string, Base>()

class Base {
   protected config: SecurequClientConfig;
   private handshakeInfo: HandshakeInfo | null = null;
   private secret_length: number = 0;
   private loadingHandshake: boolean = false;
   private handshakeErrorCount = 0

   readonly CONTENT_TYPE = "application/octet-stream";

   constructor(config: SecurequClientConfig) {
      this.config = {
         defaultOptions: {},
         ...config,
      }
      const url = new URL(config.url)
      if (url.search || url.hash) throw new Error(`Invalid config url ${config.url}. Search params or Hash url is not supported in config.url`)
      config.url = `${url.origin}${url.pathname}`

      if (Clients.has(config.url)) {
         throw new Error(`Client is Blocked!`);
      }
   }

   protected hooksCall(hook: keyof NonNullable<SecurequClientConfig['hooks']>, ...args: any[]) {
      if (this.config.hooks && this.config.hooks[hook]) {
         return (this.config.hooks[hook] as Function)(...args)
      }
   }

   protected async getSecret() {
      let full = this.config.secret
      let secret = full.substring(0, this.secret_length)
      let hash = (await crypto.hash(secret)).substring(0, this.secret_length)
      return { full, secret, hash }
   }

   protected async url(path: string) {
      const secret = await this.getSecret()
      const base = this.config.url;
      if (path === '/') path = ''
      path = path.trim();
      path = path.startsWith('/') ? path.substring(1) : path
      path = path.endsWith('/') ? path.substring(0, path.length - 1) : path
      if (path) path = `/${path}`
      return new URL(`${base}/${secret.hash}${path}`);
   }

   protected async getHeaders(path: string, init?: HttpRequestInit) {
      const url = await this.url(path)
      const secret = await this.getSecret()
      let headers: any = {};
      if (this.handshakeInfo) {
         headers["X-SIGNETURE"] = await crypto.encrypt({
            signeture: this.handshakeInfo?.signeture,
            expire: Date.now() + this.handshakeInfo!.timeDiffarenc + 10000 // 10 seconds
         }, secret.secret) || '';
      }

      headers = {
         ...this.config?.defaultOptions?.headers,
         ...init?.headers,
         ...headers,
         'Content-Type': this.CONTENT_TYPE,
         "X-ORIGIN": url.origin,
         "X-METHOD": (init?.method || 'GET').toUpperCase()
      }

      return headers;
   }

   async handshake() {
      if (this.handshakeInfo) {
         return this.handshakeInfo
      }

      if (this.loadingHandshake) {
         return await new Promise((resolve) => {
            const interval = setInterval(() => {
               if (!this.loadingHandshake) {
                  clearInterval(interval);
                  resolve(this.handshakeInfo);
               }
            }, 10);
         })
      }

      this.handshakeInfo = null;
      this.loadingHandshake = true;

      await this.hooksCall('beforeHandshake');

      let max = this.config.secret.length
      let min = Math.floor(max / 4) * 3
      this.secret_length = Math.floor(Math.random() * (max - min + 1)) + min

      const secret = await this.getSecret()
      const res = await this.fetch('/', {
         method: 'POST',
         body: {
            hash: secret.hash,
            clientTime: new Date().toISOString(),
            secret: secret.secret,
         }
      })
      if (!res.data) {
         this.handshakeErrorCount++
         if (this.handshakeErrorCount < 10) {
            await this.handshake()
         }
      }
      this.handshakeInfo = res.data
      this.loadingHandshake = false;
      this.handshakeErrorCount = 0
      await this.hooksCall('afterHandshake', this.handshakeInfo);
      return res.data
   }

   async refreshHandshake() {
      this.handshakeInfo = null
      return await this.handshake()
   }

   async fetch(path: string, init?: HttpRequestInit): Promise<SecurequClientResponse> {
      const isDev = path !== '/' && this.handshakeInfo?.dev
      const secret = await this.getSecret()
      const url = await this.url(path)
      let sparams = Object.fromEntries(url.searchParams.entries())
      const _init = { ...init }
      let params: any = {
         ...sparams,
      }

      if (Object.keys(sparams).length > 0) {
         for (let key in sparams) {
            url.searchParams.delete(key)
         }
      }

      if (_init?.params) {
         for (let key in _init.params) {
            params[key] = _init.params[key]
         }
         delete _init.params
      }

      if (Object.keys(params).length > 0) {
         if (isDev) {
            _init.params = params
         } else {
            const key = Math.random().toString(36).substring(2, 8);
            const enc = await crypto.encrypt({ params, key }, secret.secret)
            url.href = `${url.href}?${key}=${encodeURIComponent(enc)}`
         }
      }

      const httpOption: any = {
         method: "GET",
         ...this.config?.defaultOptions,
         ..._init,
         headers: await this.getHeaders(path, _init)
      }

      if (httpOption?.body) {
         if (isDev) {
            httpOption.body = JSON.stringify(httpOption.body)
         } else {
            httpOption.body = await crypto.encryptBuffer(httpOption.body, secret.secret);
         }
      }
      const res = await xanFetch(url.href, httpOption)
      if (isDev) {
         const value = await res.text()
         let val = value
         try {
            val = JSON.parse(value)
         } catch (error) {

         }
         return {
            success: res.ok,
            message: res.ok ? res.statusText : val,
            data: res.ok ? val : null,
            code: res.status
         }
      }
      const value = await res.arrayBuffer()
      const val = await crypto.decryptBuffer(new Uint8Array(value), secret.secret) as any

      return {
         success: res.ok,
         message: res.ok ? res.statusText : (val || res.statusText),
         data: res.ok ? val : null,
         code: res.status
      }
   }

}

export default Base;
